/**
 * Bulk order table:
 * - <bulk-order-view> steppers update the cart with NO "add to cart" click.
 *   Clicks are never blocked: each click updates the input + line total instantly
 *   (local math), and all pending quantity changes across every table on the page
 *   are accumulated into one shared queue and flushed to /cart/update.js in a
 *   single batched request after a short pause (matching the Trade theme's fast
 *   rapid-entry feel). The cart totals reconcile from that one response.
 * - Anchor-expand: mega-menu category links force-open the matching section's
 *   <details> and scroll to it, without collapsing any other open section.
 */
(function () {
  var FLUSH_DELAY = 500;

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.PXUTheme && window.PXUTheme.currency) {
      return window.Shopify.formatMoney(cents, window.PXUTheme.currency.money_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // ---- Shared batch queue across every bulk-order-view on the page ----
  // Rapid clicks (even across different category tables) collapse into ONE
  // /cart/update.js request, so nothing blocks and the cart is hit once per burst.
  var pendingUpdates = {}; // variantId -> target quantity
  var rowsByVariant = {}; // variantId -> row element (for reconciling the response)
  var flushTimer = null;
  var inFlight = false;

  function scheduleFlush() {
    clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, FLUSH_DELAY);
  }

  function flush() {
    if (inFlight) {
      // A request is already running; retry once it settles so we don't send
      // overlapping cart mutations.
      scheduleFlush();
      return;
    }

    var variantIds = Object.keys(pendingUpdates);
    if (!variantIds.length) return;

    var updates = {};
    variantIds.forEach(function (id) {
      updates[id] = pendingUpdates[id];
      delete pendingUpdates[id];
    });

    inFlight = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
      })
      .then(function (cart) {
        variantIds.forEach(function (id) {
          var row = rowsByVariant[id];
          if (!row) return;
          var item = cart.items.find(function (line) {
            return String(line.variant_id) === id;
          });
          var totalEl = row.querySelector('[data-bulk-line-total]');
          if (totalEl) totalEl.textContent = formatMoney(item ? item.final_line_price : 0);
          row.classList.remove('bulk-order-view__item--syncing', 'bulk-order-view__item--error');
        });

        document.dispatchEvent(new CustomEvent('bulk:cart-updated', { detail: { cart: cart } }));

        if (window.PXUTheme && window.PXUTheme.jsAjaxCart) {
          window.PXUTheme.jsAjaxCart.updateView();
        }
      })
      .catch(function (error) {
        variantIds.forEach(function (id) {
          var row = rowsByVariant[id];
          if (!row) return;
          row.classList.remove('bulk-order-view__item--syncing');
          row.classList.add('bulk-order-view__item--error');
        });
        console.error(error);
      })
      .finally(function () {
        inFlight = false;
        // Anything queued while the request was in flight flushes next.
        if (Object.keys(pendingUpdates).length) scheduleFlush();
      });
  }

  function queueChange(row, quantity) {
    var variantId = row.dataset.variantId;
    pendingUpdates[variantId] = quantity;
    rowsByVariant[variantId] = row;
    row.dataset.cartQuantity = quantity;

    // Optimistic per-row line total so the number reacts instantly to each click,
    // without waiting for (or blocking on) the batched request.
    var unit = parseInt(row.dataset.unitPrice, 10);
    var totalEl = row.querySelector('[data-bulk-line-total]');
    if (totalEl && !isNaN(unit)) totalEl.textContent = formatMoney(unit * quantity);

    // Show the remove-line control only once the line has a quantity.
    var removeBtn = row.querySelector('[data-bulk-remove]');
    if (removeBtn) removeBtn.toggleAttribute('hidden', quantity <= 0);

    row.classList.remove('bulk-order-view__item--error');
    row.classList.add('bulk-order-view__item--syncing');
    scheduleFlush();
  }

  if (!customElements.get('bulk-order-view')) {
    class BulkOrderView extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.handleClick.bind(this));
        this.addEventListener('change', this.handleChange.bind(this));
        this.addEventListener('keydown', this.handleKeydown.bind(this));
      }

      handleKeydown(event) {
        if (event.key === 'Enter' && event.target.matches('[data-bulk-qty-input]')) {
          event.preventDefault();
          event.target.blur();
        }
      }

      handleClick(event) {
        // Remove-line control: set this product's quantity to 0 (removes the line).
        const removeTarget = event.target.closest('[data-bulk-remove]');
        if (removeTarget) {
          event.preventDefault();
          const row = removeTarget.closest('[data-bulk-item]');
          const input = row.querySelector('[data-bulk-qty-input]');
          input.value = 0;
          this.applyLocalState(row, 0, parseInt(input.min, 10) || 0);
          queueChange(row, 0);
          return;
        }

        const increaseTarget = event.target.closest('[data-bulk-qty-increase]');
        const decreaseTarget = event.target.closest('[data-bulk-qty-decrease]');
        if (!increaseTarget && !decreaseTarget) return;
        if (decreaseTarget && decreaseTarget.hasAttribute('disabled')) return;

        const row = event.target.closest('[data-bulk-item]');
        const input = row.querySelector('[data-bulk-qty-input]');
        const min = parseInt(input.min, 10) || 0;
        let value = parseInt(input.value, 10);
        if (isNaN(value)) value = min;

        value = increaseTarget ? value + 1 : Math.max(min, value - 1);
        input.value = value;

        this.applyLocalState(row, value, min);
        queueChange(row, value);
      }

      handleChange(event) {
        if (!event.target.matches('[data-bulk-qty-input]')) return;

        const row = event.target.closest('[data-bulk-item]');
        const input = event.target;
        const min = parseInt(input.min, 10) || 0;
        let value = parseInt(input.value, 10);
        if (isNaN(value) || value < min) {
          value = min;
          input.value = value;
        }

        this.applyLocalState(row, value, min);
        queueChange(row, value);
      }

      applyLocalState(row, value, min) {
        const decreaseBtn = row.querySelector('[data-bulk-qty-decrease]');
        if (decreaseBtn) decreaseBtn.toggleAttribute('disabled', value <= min);
      }
    }

    customElements.define('bulk-order-view', BulkOrderView);
  }

  // "Add one of each" quick-add: bumps every available product in the section by
  // one and lets the shared batch queue send it all in a single /cart/update.js.
  // The button lives in the static header (outside <summary>), so it never toggles
  // the disclosure. Registered once at module scope.
  if (!window.bulkQuickAddInit) {
    window.bulkQuickAddInit = true;

    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-bulk-add-one-each]');
      if (!btn) return;
      event.preventDefault();

      var scope = btn.closest('[data-bulk-anchor]') || document;
      scope.querySelectorAll('[data-bulk-item]').forEach(function (row) {
        var input = row.querySelector('[data-bulk-qty-input]');
        if (!input) return; // unavailable / sold out — no stepper to bump

        var min = parseInt(input.min, 10) || 0;
        var value = parseInt(input.value, 10);
        if (isNaN(value)) value = min;
        value = value + 1;
        input.value = value;

        var decreaseBtn = row.querySelector('[data-bulk-qty-decrease]');
        if (decreaseBtn) decreaseBtn.toggleAttribute('disabled', value <= min);

        queueChange(row, value);
      });
    });
  }

  // Anchor-expand, shared across every bulk-order-view section on the page.
  if (!window.bulkAnchorExpandInit) {
    window.bulkAnchorExpandInit = true;

    const expandAndScroll = (hash, instant) => {
      if (!hash) return false;
      const id = hash.replace(/^#/, '');
      if (!id) return false;

      const target = document.querySelector(`[data-bulk-anchor="${CSS.escape(id)}"]`);
      if (!target) return false;

      const details = target.querySelector('details');
      if (details) details.open = true;

      target.scrollIntoView({
        behavior: instant ? 'auto' : 'smooth',
        block: 'start',
      });

      return true;
    };

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="#"]');
      if (!link) return;

      const url = new URL(link.href, window.location.href);
      if (url.pathname !== window.location.pathname || !url.hash) return;

      if (expandAndScroll(url.hash, false)) {
        event.preventDefault();
        history.pushState(null, '', url.hash);
      }
    });

    if (window.location.hash) {
      window.addEventListener('DOMContentLoaded', () => {
        expandAndScroll(window.location.hash, true);
      });
    }
  }
})();
